import React from 'react';

import { Add as AddIcon } from '@mui/icons-material';
import { Button } from '@mui/material';
import { isNotNil } from 'es-toolkit';

import { PeoCompanyType, PeoOrderBy } from 'src/api';
import { TableTopAction } from 'src/components/table';
import { COMPANY_SEARCH_CATEGORIES } from 'src/constants/company';
import { FilterKeys } from 'src/constants/filter';
import { ModalId } from 'src/constants/modals';
import { useFilterStorage, useSearchQueryVariables } from 'src/hooks/common';
import { usePeosList } from 'src/hooks/peo/usePeosList';
import { useModal } from 'src/hooks/shared';
import { createOrderByQueryVariable } from 'src/utils/gql';
import { preparePeosFilterByStatus } from 'src/utils/peo';

import { PEOS_TABLE_CV_COLUMNS } from './constants';
import { PeosInnerTable } from './PeosInnerTable';
import { prepareSearchFilterByCategories } from './utils/filter';

export const PeosTableCVStaff: React.FC = () => {
  const { openModal } = useModal();

  const {
    getFilterByKey,
    getSortOptionByKey,
    getPaginationQueryVariablesByKey,
    getPaginationObjectByKey,
  } = useFilterStorage();
  const pagination = getPaginationObjectByKey(FilterKeys.PeosTableCVStaff);

  const filterQueryVariables = preparePeosFilterByStatus(
    getFilterByKey(FilterKeys.PeosTableCVStaff),
  );
  const searchQueryVariables = useSearchQueryVariables(prepareSearchFilterByCategories);

  const { peosList, peosCount, loading, refetch } = usePeosList(
    {
      AND: [
        { companyType: { equals: PeoCompanyType.Peo } },
        searchQueryVariables,
        filterQueryVariables,
      ].filter(isNotNil),
    },
    createOrderByQueryVariable(getSortOptionByKey(FilterKeys.PeosTableCVStaff)) as PeoOrderBy,
    getPaginationQueryVariablesByKey(FilterKeys.PeosTableCVStaff),
    true,
  );

  const refetchData = React.useCallback(async () => {
    await refetch();
    pagination.resetPage();
  }, [pagination, refetch]);

  const topActions = React.useMemo(
    (): Array<TableTopAction> => [
      {
        actionName: 'Add',
        onAction: () => {
          openModal(ModalId.InvitePeoByCVStaffModal);
        },
        icon: <AddIcon />,
        show: true,
        render() {
          return (
            <Button
              variant="contained"
              startIcon={this.icon}
              disabled={loading}
              loading={loading}
              onClick={this.onAction}
            >
              New
            </Button>
          );
        },
      },
    ],
    [loading, openModal],
  );

  return (
    <PeosInnerTable
      peosList={peosList}
      peosCount={peosCount}
      loading={loading}
      columns={PEOS_TABLE_CV_COLUMNS}
      topActions={topActions}
      onRefresh={refetchData}
      pagination={pagination}
      keyFilter={FilterKeys.PeosTableCVStaff}
      searchCategories={COMPANY_SEARCH_CATEGORIES}
    />
  );
};
